import React, { useState, useRef } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { api } from "../lib/api";
import { Moon, Sun, User, Lock, Save, Camera, Check, Shield, LogOut, Crown } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const fileRef = useRef(null);

  const [profile, setProfile] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    availability: user?.availability || "available",
    avatar: user?.avatar || "",
  });
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);

  async function saveProfile() {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", {
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        availability: profile.availability,
        avatar: profile.avatar,
      });
      setUser(data);
      toast.success("Profil gespeichert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  }

  function onAvatar(e) {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = ev => setProfile(p => ({ ...p, avatar: ev.target.result }));
    reader.readAsDataURL(f);
  }

  async function changePassword(e) {
    e.preventDefault();
    if (pw.next.length < 6) { toast.error("Neues Passwort mindestens 6 Zeichen"); return; }
    if (pw.next !== pw.confirm) { toast.error("Passwörter stimmen nicht überein"); return; }
    setChangingPw(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pw.current, new_password: pw.next,
      });
      if (data.access_token) localStorage.setItem("ff_token", data.access_token);
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Passwort geändert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    } finally {
      setChangingPw(false);
    }
  }

  function doLogout() {
    logout(); nav("/login");
  }

  const avatar = profile.avatar || user?.avatar;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="settings-page">
      <div>
        <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Konto</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Einstellungen</h1>
        <p className="text-muted-foreground mt-1">Verwalten Sie Ihr Profil, Ihr Passwort und Ihre Präferenzen.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile */}
        <Card className="rounded-3xl p-6 border-border/60 glass space-y-5" data-testid="profile-card">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-xl font-semibold">Profil</h3>
            {user?.is_owner && <Badge className="rounded-full ml-auto text-[10px] bg-amber-500/20 text-amber-500 border-amber-500/30"><Crown className="w-3 h-3 mr-1 inline" />Inhaber</Badge>}
          </div>

          <div className="flex items-center gap-4">
            <div className="relative">
              {avatar ? (
                <img src={avatar} alt="" className="w-20 h-20 rounded-3xl object-cover border border-border" />
              ) : (
                <div className="w-20 h-20 rounded-3xl bg-primary/15 text-primary flex items-center justify-center text-3xl font-heading font-bold">
                  {profile.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <button onClick={()=>fileRef.current?.click()}
                      className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:scale-110 transition"
                      data-testid="upload-avatar">
                <Camera className="w-4 h-4" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatar} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-lg font-semibold truncate">{profile.name || "Unbenannt"}</div>
              <div className="text-sm text-muted-foreground capitalize">{user?.role}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={profile.name} onChange={e=>setProfile({...profile, name:e.target.value})}
                     className="rounded-full h-10 px-4 mt-1" data-testid="profile-name" />
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input type="email" value={profile.email} onChange={e=>setProfile({...profile, email:e.target.value})}
                     className="rounded-full h-10 px-4 mt-1" data-testid="profile-email" />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={profile.phone} onChange={e=>setProfile({...profile, phone:e.target.value})}
                     className="rounded-full h-10 px-4 mt-1" data-testid="profile-phone" />
            </div>
            <div>
              <Label>Verfügbarkeit</Label>
              <select value={profile.availability}
                      onChange={e=>setProfile({...profile, availability:e.target.value})}
                      className="w-full rounded-full h-10 px-4 mt-1 bg-background border border-input text-sm"
                      data-testid="profile-availability">
                <option value="available">Verfügbar</option>
                <option value="busy">Beschäftigt</option>
                <option value="offline">Offline</option>
                <option value="vacation">Urlaub</option>
              </select>
            </div>
          </div>

          <Button onClick={saveProfile} disabled={saving} className="rounded-full" data-testid="save-profile">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Speichern..." : "Profil speichern"}
          </Button>
        </Card>

        {/* Password */}
        <Card className="rounded-3xl p-6 border-border/60 glass space-y-4" data-testid="password-card">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-xl font-semibold">Passwort</h3>
          </div>
          <p className="text-sm text-muted-foreground">Ändern Sie Ihr Passwort regelmäßig. Mindestens 6 Zeichen.</p>
          <form onSubmit={changePassword} className="space-y-3" data-testid="password-form">
            <div>
              <Label>Aktuelles Passwort</Label>
              <Input type="password" value={pw.current} onChange={e=>setPw({...pw, current:e.target.value})}
                     required className="rounded-full h-10 px-4 mt-1" data-testid="pw-current" />
            </div>
            <div>
              <Label>Neues Passwort</Label>
              <Input type="password" value={pw.next} onChange={e=>setPw({...pw, next:e.target.value})}
                     required minLength={6} className="rounded-full h-10 px-4 mt-1" data-testid="pw-new" />
            </div>
            <div>
              <Label>Neues Passwort bestätigen</Label>
              <Input type="password" value={pw.confirm} onChange={e=>setPw({...pw, confirm:e.target.value})}
                     required className="rounded-full h-10 px-4 mt-1" data-testid="pw-confirm" />
              {pw.next && pw.confirm && pw.next !== pw.confirm && (
                <p className="text-xs text-destructive mt-1">Passwörter stimmen nicht überein</p>
              )}
              {pw.next && pw.confirm && pw.next === pw.confirm && pw.next.length >= 6 && (
                <p className="text-xs text-accent mt-1 flex items-center gap-1"><Check className="w-3 h-3" /> Passwörter stimmen überein</p>
              )}
            </div>
            <Button type="submit" disabled={changingPw} className="rounded-full" data-testid="change-pw-btn">
              <Shield className="w-4 h-4 mr-2" /> {changingPw ? "Wird geändert..." : "Passwort ändern"}
            </Button>
          </form>
        </Card>

        {/* Appearance */}
        <Card className="rounded-3xl p-6 border-border/60 glass space-y-4">
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
            {theme === "dark" ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}
            Erscheinungsbild
          </h3>
          <div className="flex items-center justify-between p-4 rounded-2xl bg-secondary/40">
            <div>
              <div className="font-medium">Dark Mode</div>
              <div className="text-xs text-muted-foreground">Umschalten zwischen Hell und Dunkel</div>
            </div>
            <Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="settings-theme-switch" />
          </div>
        </Card>

        {/* Danger zone / Session */}
        <Card className="rounded-3xl p-6 border-border/60 glass space-y-4">
          <h3 className="font-heading text-xl font-semibold">Sitzung</h3>
          <p className="text-sm text-muted-foreground">
            Eingeloggt als <b className="text-foreground">{user?.email}</b>
          </p>
          <Separator />
          <Button variant="outline" onClick={doLogout} className="rounded-full w-full border-destructive/30 text-destructive hover:bg-destructive/10" data-testid="logout-settings">
            <LogOut className="w-4 h-4 mr-2" /> Abmelden
          </Button>
        </Card>
      </div>
    </div>
  );
}
